import {
  cn,
  Flex,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from "@suns/design-system";
// eslint-disable-next-line no-restricted-imports -- DO NOT COPY THIS LINE. See docs/lints.md.
import {
  StatsQueryParams,
  StatShotProfile,
} from "@suns/api/generated-client/apollo";
import { useApolloStats } from "../ApolloStatsTable/hooks/useApolloStats";
import { SunsApiError } from "@suns/api";
import {
  StatsGrouping,
  StatsObject,
  StatsType,
} from "../ApolloStatsTable/const";
import { DataTable } from "@suns/design-system/src/components/DataTable/DataTable";
import { shotProfileColumns } from "./columns";

export interface ShotProfile {
  region: string;
  fgm: number | null;
  fga: number | null;
  fgPct: number | null;
  efgPct: number | null;
  share: number | null;
}

export function ApolloShotProfileTable({
  playerId,
  size = "md",
}: {
  playerId: number;
  size?: "sm" | "md";
}) {
  const title = "Shot Profile";
  const { response, loading, error } = useApolloStats(
    StatsObject.PLAYER,
    StatsGrouping.TOTAL,
    {
      playerId,
      format: StatsQueryParams.format.TOTALS,
      types: [StatsType.SHOT_PROFILE],
    }
  );

  if (loading) {
    return <SkeletonTable title={title} />;
  }

  if (error) {
    throw new SunsApiError("Unable to load shot profile.", {
      cause: error,
    });
  }

  const shotProfileData = response.stats[0]?.shotProfile;
  if (
    !shotProfileData ||
    Object.values(shotProfileData).every((val) => val == null)
  ) {
    return null;
  }

  const shotProfileRows = convertShotProfileDataToRows(shotProfileData);

  return (
    <>
      <Flex direction="down" gap="sm">
        <Text heading muted>
          {title}
        </Text>
        <DataTable
          data={shotProfileRows}
          columns={shotProfileColumns}
          className={cn(
            size === "md"
              ? ["[&_td]:py-2", "[&_th]:py-2"]
              : [
                  "[&_th]:font-bold",
                  "[&_th]:text-[9px]",
                  "even:[&_tr]:bg-gray-50",
                  "[&_tr]:text-[9px]",
                  "[&_th]:py-0",
                  "[&_td]:py-0",
                ]
          )}
        />
      </Flex>
    </>
  );
}

function convertShotProfileDataToRows(
  shotProfile: StatShotProfile
): ShotProfile[] {
  const regions = [
    { key: "restrictedArea", label: "Restricted Area" },
    { key: "paint", label: "Paint" },
    { key: "shortMidrange", label: "Short Midrange" },
    { key: "longMidrange", label: "Long Midrange" },
    { key: "corner", label: "Corner 3" },
    { key: "aboveBreak", label: "Above the Break" },
    { key: "deep", label: "Deep 3" },
    { key: "nba3", label: "NBA 3" },
  ] as const;

  return regions.map(({ key, label }) => {
    const region = {
      region: label,
      fgm: shotProfile[`${key}Fgm`],
      fga: shotProfile[`${key}Fga`],
      fgPct: shotProfile[`${key}Pct`],
      efgPct: shotProfile[`${key}EfgPct`],
      share: shotProfile[`${key}Share`],
    };

    return region;
  });
}

export function SkeletonTable({ title }: { title?: string }) {
  return (
    <Flex direction="down" gap="md">
      <Text heading variant="muted">
        {title}
      </Text>
      <Table
        className={cn("rounded border", [
          "max-md:[&_td]:p-2",
          "max-md:[&_td]:text-sm",
          "max-md:[&_th]:p-2",
          "max-md:[&_th]:text-xs",
        ])}
      >
        <TableHeader>
          <TableRow>
            {Array.from({ length: 18 }, (_, i) => i + 1).map((_, index) => (
              <TableHead className="px-[0.85rem]" key={index}>
                <Skeleton className="h-4 w-full" />
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 6 }, (_, i) => i + 1).map((i) => (
            <TableRow key={`skeleton-row-${i}`}>
              {Array.from({ length: 18 }, (_, i) => i + 1).map((_, index) => (
                <TableCell
                  className={cn(index !== 0 && "border-l")}
                  key={index}
                >
                  <Skeleton className="h-4 w-10" />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Flex>
  );
}
